// The right-click menus: ONE shared core (the clipboard and selection items any
// object's menu starts with), then the kind's own items; the menu itself.
// docs/claude/selection.md.
import {
  type ShortcutId,
  shortcutLabel,
} from '../../shared/shortcuts';
import {
  MULTI_KINDS,
  selectedCount,
  selectedOf,
} from '../selection';
import {
  type ProductInstance,
  type Vec2,
} from '../../shared/types';
import { productParts } from './catalogSync';
import { hitConnection } from './connectionUi';
import {
  constraintBadgeBoxes,
  constraintGeom,
  isFixedObject,
  pickConstraint,
  selectedConstraint,
  toggleFix,
} from './constraintUi';
import {
  selectedColorTargets,
  startCopyColors,
} from './copyColors';
import {
  renderProperties,
  state,
  vp,
} from './core';
import { explodable } from './explodeJoin';
import { groupMembers } from '../groups';
import { tr } from '../i18n';
import { badgeAt } from '../objConstraints';
import {
  openProductModalForInstance,
  saveGroupAsProduct,
} from './productEditor';
import { scaleTextComment } from './textBoxes';
import {
  $,
  addConnectionBend,
  applyClickTake,
  canEditStandards,
  cutSelection,
  duplicateSelection,
  groupSelection,
  hideCtxMenu,
  objectStackAt,
  persistViewPrefs,
  pointOverSelection,
  reverseSelectedConnection,
  runCommandButton,
  screenPos,
  selectedInstance,
  selectedObjectIds,
  selectedWholeGroup,
  selectionHoldsGroup,
  SPACES,
  ungroupSelection,
  unrelateParts,
  updateProductFromCatalogue,
  zoomToSelection,
} from '../main';
import { type ClipSpace } from './clipboard';
import { clickTake } from '../pickStack';

// ---- Context menus: ONE shared core, then the kind's own items ----
// Every object's right-click menu, in every space, is built by openContextMenu
// from the same core (Cut · Copy · Paste · Duplicate · Delete | the four Order
// steps | Zoom to · Properties) and then the items only that kind has. The core
// acts on whichever space the click was in; items that can't act are greyed,
// not hidden, so the menu keeps its shape.

/** One menu line: its English label (translated when shown), what it does, and
 *  the shortcut that does the same (its key is shown on the right). */
export interface CtxItem { label: string; run: () => void; enabled?: boolean; key?: ShortcutId }

/** Open the right-click menu at the event: groups of items, separated. */
export function openContextMenu(e: MouseEvent, groups: CtxItem[][]): void {
  const menu = $('ctx-menu');
  menu.innerHTML = '';
  for (const g of groups) {
    if (!g.length) continue;
    if (menu.children.length) { const d = document.createElement('div'); d.className = 'sep'; menu.appendChild(d); }
    for (const it of g) {
      const b = document.createElement('button');
      const name = document.createElement('span');
      name.textContent = tr(it.label);
      b.appendChild(name);
      if (it.key) {
        const k = document.createElement('span');
        k.className = 'ctx-key';
        k.textContent = shortcutLabel(it.key, tr);
        b.appendChild(k);
      }
      b.dataset.item = it.label;
      b.disabled = it.enabled === false;
      if (!b.disabled) b.addEventListener('click', () => { hideCtxMenu(); it.run(); });
      menu.appendChild(b);
    }
  }
  menu.style.left = Math.min(e.clientX, window.innerWidth - 230) + 'px';
  menu.style.top = Math.min(e.clientY, window.innerHeight - menu.children.length * 30 - 16) + 'px';
  menu.classList.remove('hidden');
}

/** The core every menu starts with, acting on the space the click was in.
 *  `at` is where a Paste lands (world in the model, paper mm on a sheet). */
export function coreMenuItems(space: ClipSpace, at?: Vec2): CtxItem[][] {
  const sp = SPACES[space];
  const has = sp.has();
  // A lone connection can be deleted and zoomed to; it is copied with its objects.
  const hasConn = space === 'model' && !!state.selectedConnId;
  const hasCon = space === 'model' && !!selectedConstraint();
  const canPaste = sp.canPaste();
  const copy = (): void => sp.copy();
  const paste = (): void => sp.paste(at);
  const del = (): void => sp.del();
  const orderBtn = (id: string): { run: () => void; enabled: boolean } => ({
    run: () => document.getElementById(id)?.click(),
    enabled: !((document.getElementById(id) as HTMLButtonElement | null)?.disabled ?? true),
  });
  return [
    [
      { label: 'Cut', run: cutSelection, enabled: has, key: 'cut' },
      { label: 'Copy', run: copy, enabled: has, key: 'copy' },
      { label: 'Paste', run: paste, enabled: canPaste, key: 'paste' },
      { label: 'Duplicate', run: duplicateSelection, enabled: has, key: 'duplicate' },
      { label: 'Delete', run: del, enabled: has || hasConn || hasCon, key: 'delete' },
    ],
    space === 'title' ? [] : [
      { label: 'Bring to front', ...orderBtn('btn-front') },
      { label: 'Bring forward', ...orderBtn('btn-forward') },
      { label: 'Send backward', ...orderBtn('btn-backward') },
      { label: 'Send to back', ...orderBtn('btn-back') },
    ],
    [
      ...(space === 'model' ? [{ label: 'Zoom to', run: zoomToSelection, enabled: has || hasConn }] : []),
      { label: 'Properties', run: showPropertiesPanel, key: 'properties' },
    ],
  ];
}

/** The Properties panel, shown and on its Properties tab. */
function showPropertiesPanel(): void {
  $('app').classList.remove('props-hidden');
  state.propTab = 'properties';
  persistViewPrefs();
  renderProperties();
}

/** Right-click in the model: acts on (and selects) the object under the cursor,
 *  or the current selection — the core, then what only this kind of object has. */
export function showObjectContextMenu(e: MouseEvent): void {
  const world = vp.screenToWorld(screenPos(e));
  // A constraint badge: its constraint's menu.
  const cid = badgeAt(constraintBadgeBoxes, screenPos(e));
  if (cid) { pickConstraint(cid); openContextMenu(e, coreMenuItems('model', world)); return; }
  // Right-clicking outside the current selection selects what a left click there
  // would take (`clickTake`: a dimension over a shape, a group for its member, a
  // room's feature).
  if (!(selectedCount(state, MULTI_KINDS) > 0 && pointOverSelection(world))) {
    const head = objectStackAt(world, screenPos(e)).head;
    if (head) applyClickTake(clickTake(state.drawing, head, selectedObjectIds()));
  }
  const own: CtxItem[] = [];
  const conn = state.selectedConnId ? state.drawing.connections.find((c) => c.id === state.selectedConnId) : undefined;
  if (conn) {
    own.push({ label: 'Reverse direction', run: () => reverseSelectedConnection(conn) });
    const at = hitConnection(world, 8);
    if (at?.conn === conn) own.push({ label: 'Add bend here', run: () => addConnectionBend(conn, at.seg, world) });
  }
  const texts = selectedOf(state, 'text');
  const text = texts.length === 1 && selectedCount(state, MULTI_KINDS) === 1 ? (state.drawing.texts || []).find((t) => t.id === texts[0]) : undefined;
  if (text) {
    own.push({ label: 'Enlarge (×2)', run: () => scaleTextComment(text, 2) });
    own.push({ label: 'Reduce (÷2)', run: () => scaleTextComment(text, 0.5) });
  }
  // Only ever for a SINGLE placed part: the editor describes one product, so it
  // has nothing coherent to show for a mixed or multiple selection.
  const oneInst = selectedOf(state, 'instance').length === 1 && selectedCount(state, MULTI_KINDS) === 1 ? selectedInstance() : null;
  // Same wording rule as the catalogue card's menu: without the `edit` claim the
  // window opens to look at, not to change.
  if (oneInst) own.push({ label: canEditStandards() ? 'Edit properties…' : 'View properties…', run: () => openProductModalForInstance(oneInst) });
  const insts = selectedOf(state, 'instance').map((id) => state.drawing.instances.find((i) => i.id === id)).filter((i): i is ProductInstance => !!i);
  if (selectedCount(state, MULTI_KINDS) >= 2) own.push({ label: 'Group', run: groupSelection, key: 'group' });
  if (selectionHoldsGroup()) own.push({ label: 'Ungroup', run: () => { void ungroupSelection(); }, key: 'ungroup' });
  // A placed product (its whole linked group selected): re-lay it from its
  // catalogue product; a plain group of parts: make a catalogue product of it.
  const whole = selectedWholeGroup();
  const tmpl = whole?.source ? state.templates.find((t) => t.id === whole.source!.templateId) : undefined;
  if (whole && tmpl) own.push({ label: 'Update from catalogue', run: () => updateProductFromCatalogue(whole.id, tmpl) });
  if (whole && !whole.source && canEditStandards() && productParts(whole.id).length === groupMembers(state.drawing, whole.id).length
    && productParts(whole.id).every((i) => !i.autoBenches && !i.roomFixture)) {
    own.push({ label: 'Save as product…', run: () => saveGroupAsProduct(whole.id) });
  }
  if (insts.some((i) => i.relId)) own.push({ label: 'Remove relation', run: () => unrelateParts(insts) });
  const ex = explodable();
  if (ex.polys.length + ex.walls.length > 0) own.push({ label: 'Explode', run: () => runCommandButton('btn-explode') });
  if (selectedOf(state, 'wall').length >= 2) own.push({ label: 'Join', run: () => runCommandButton('btn-join') });
  const colTargets = selectedColorTargets();
  if (colTargets.length) own.push({ label: '🎨 Copy colours from…', run: () => startCopyColors(colTargets) });
  // A single object can be fixed in place: no constraint (and no drag) moves it.
  const oneObj = colTargets.length === 1 ? colTargets[0].obj.id : null;
  if (!colTargets.length && state.selectedSectionId) {
    const sid = state.selectedSectionId;
    own.push({ label: isFixedObject(sid) ? 'Unfix' : 'Fix in place', run: () => toggleFix(sid) });
  }
  if (oneObj && constraintGeom(oneObj)) {
    if (isFixedObject(oneObj)) own.push({ label: 'Unfix', run: () => toggleFix(oneObj) });
    else own.push({ label: 'Fix in place', run: () => toggleFix(oneObj) });
  }
  openContextMenu(e, [...coreMenuItems('model', world), own]);
}
